import React, { useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CHART_COLORS, STYLE_COLORS } from '../../chatView/components/artifacts/widgetOptions';
import TruncatedNote from './TruncatedNote';
import { TRANSLATION_KEY } from '../constants';
import { trendViewShape } from '../propShapes';
import { formatVerdictValue } from '../verdictFormat';
import css from './TrendChart.css';

const CHART_HEIGHT = 140;

const AXIS_LABEL_STYLE = { color: STYLE_COLORS.textLight, fontSize: '10px' };

export const buildTrendChartOptions = view => ({
    chart: {
        type: 'line',
        height: CHART_HEIGHT,
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        spacing: [8, 4, 2, 4],
    },
    colors: CHART_COLORS,
    credits: { enabled: false },
    title: { text: null },
    xAxis: {
        type: 'datetime',
        tickLength: 0,
        lineColor: STYLE_COLORS.gridLine,
        labels: { style: AXIS_LABEL_STYLE },
    },
    yAxis: {
        title: { text: null },
        gridLineColor: STYLE_COLORS.gridLine,
        labels: {
            style: AXIS_LABEL_STYLE,
            formatter() {
                return formatVerdictValue(this.value, view.series[0]?.display_format);
            },
        },
    },
    legend:
        view.series.length > 1
            ? { enabled: true, itemStyle: { color: STYLE_COLORS.text, fontSize: '11px' }, margin: 4 }
            : { enabled: false },
    tooltip: {
        backgroundColor: STYLE_COLORS.background,
        borderColor: STYLE_COLORS.border,
        style: { color: STYLE_COLORS.text },
        xDateFormat: '%b %e',
        pointFormatter() {
            return `${this.series.name}: <b>${formatVerdictValue(this.y, this.series.options.displayFormat)}</b>`;
        },
    },
    plotOptions: {
        series: { marker: { enabled: false }, lineWidth: 2, animation: false },
    },
    series: view.series.map(series => ({
        name: series.label,
        displayFormat: series.display_format,
        data: series.points.map(point => [Date.parse(point.timestamp), point.value]),
    })),
});

const TrendChart = ({ view }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const options = useMemo(() => buildTrendChartOptions(view), [view]);

    useEffect(() => {
        if (!containerRef.current) return undefined;

        const resizeObserver = new ResizeObserver(() => {
            if (chartRef.current && chartRef.current.chart) {
                chartRef.current.chart.reflow();
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div>
            <div
                ref={containerRef}
                className={css.trendChart}
                role="img"
                aria-label={view.series.map(series => series.label).join(', ')}
            >
                <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={options}
                    containerProps={{ style: { width: '100%', height: `${CHART_HEIGHT}px` } }}
                />
            </div>
            <TruncatedNote count={view.truncated_count} translationId={`${TRANSLATION_KEY}.CARD.MORE_SERIES`} />
        </div>
    );
};

TrendChart.propTypes = {
    view: trendViewShape.isRequired,
};

export default TrendChart;
